import { Flex, Skeleton, Typography } from 'antd'
import { FunctionComponent } from 'react'
import styled from 'styled-components'
import { KolThumbnail } from '@/components/common/kol-thumbnail'
import Navigation from '@/components/kol/common/kol-cooperation-detail-drawer/navigation'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import type { KolActionsDropdownProps } from '@/components/kol/navigation/kol-actions-dropdown'
import SafeNewTabLink from '@/components/safe-new-tab-link'
import routes from '@/hooks/use-authorization/routes'
import useCustomizedTag from '@/hooks/use-customized-tag'
import { useIntl } from '@/i18n/hooks/use-intl'

interface HeaderProps {
  kolId: string
  from?: KolActionsDropdownProps['from']
}

const Header: FunctionComponent<HeaderProps> = ({ kolId, from }) => {
  const { formatMessage } = useIntl()

  const { kolInfo, isLocked, isFetching } = useKolInfo(kolId)
  const { getCustomizedTagLocalizedName } = useCustomizedTag()

  const localizedCustomizedTagsNames = kolInfo?.customizedTagsNames.map(
    getCustomizedTagLocalizedName,
  )
  const joinedLocalizedCustomizedTagsNames = [
    formatMessage({ id: 'search:filter_option_kol_type' }),
    formatMessage({ id: 'general:colon' }),
    localizedCustomizedTagsNames?.length
      ? localizedCustomizedTagsNames.join(' · ')
      : formatMessage({
          id: 'autoreport:chart_label_empty_data',
        }),
  ].join('')
  const kolDetailLink = routes.kolId.stringifyUrl(
    {
      kolId,
    },
    {
      isClickedFromDrawer: true,
      from,
    },
  )

  return (
    <Flex vertical gap={16}>
      <Navigation
        from={from}
        isKolInfoLocked={isLocked}
        kolId={kolId}
        kolInfo={kolInfo}
      />
      <KolInfoWrapper>
        <Skeleton
          active
          avatar={{ size: 80 }}
          loading={isFetching}
          paragraph={{ width: ['100%', '80%'] }}
          title={false}
        >
          <Flex align='center' gap={16}>
            <SafeNewTabLink href={kolDetailLink}>
              <KolThumbnail
                imageSize='medium'
                isRadarStar={kolInfo?.isRadarStar}
                isVerified={kolInfo?.verified}
                kolUuid={kolId}
              />
            </SafeNewTabLink>
            <Flex vertical gap={8} style={{ overflow: 'hidden' }}>
              <KolName href={kolDetailLink}>{kolInfo?.name}</KolName>
              <Typography.Text
                ellipsis={{ tooltip: joinedLocalizedCustomizedTagsNames }}
                type='secondary'
              >
                {joinedLocalizedCustomizedTagsNames}
              </Typography.Text>
            </Flex>
          </Flex>
        </Skeleton>
      </KolInfoWrapper>
    </Flex>
  )
}

const KolInfoWrapper = styled.div`
  font-weight: 400;
  padding: 0 24px;

  .ant-skeleton {
    display: flex;
    align-items: center;
  }

  .ant-skeleton .ant-skeleton-content .ant-skeleton-paragraph {
    margin: 0;

    > li {
      --ant-skeleton-paragraph-li-height: 16px;
      margin-block-start: 12px;
      border-radius: 0;

      &:first-child {
        margin: 0;
      }
    }
  }
`

const KolName = styled(SafeNewTabLink)`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
`

export default Header
